<template>
    <mini-calculator-page>
        <div slot="summary-panel">
            <div class="p-animated-calc__results a-form-inputs u-full-width u-margin-auto">
                <mini-calculator-header title="Dependency Multiplicand" icon="dependency-multiplicand"/>

                <control for="deceased_income" label="Deceased Net income" before-after="pound">
                    <input autocomplete="off" v-model.number="deceasedNetIncome"
                           name="deceased_income"
                           ref="deceasedNetIncome"
                           v-field-control:deceased_income.required="{type:'number', min: 0}"
                           id="deceasedIncome"
                           v-on:focus="deceasedNetIncome = deceasedNetIncome || ''"
                           v-on:blur="deceasedNetIncome = deceasedNetIncome || 0"
                           v-two-dp
                           min="0"
                           type="number"/>
                    <control-error :show="$error('deceased_income')">
                        The Deceased Net income is required and must be a valid amount
                    </control-error>
                </control>

                <dependant-income
                        :reference="'dependentIncome'+index"
                        :ref="'dependentIncome'+index"
                        @deleteDependant="removeDependant(index)"
                        @addNewDependant="addDependant"
                        v-for="(item, index) in getValue('dependants')"
                        :showAddButton="true"
                        :columnTwo="true"
                        :index="index"></dependant-income>

                <label>
                    Multiplicand
                </label>
                
                <div class="p-dependency-calc__slider">
                    <vue-slider ref="slider" :min="1" :max="100"
                        :real-time="true"
                        :clickable="true"
                        v-model="multiplicand"
                        :piecewise="true"
                        :process-style="{'backgroundColor' : '#4A89E5'}"
                        :tooltip-style="{'backgroundColor' : '#4A89E5', 'borderColor' : '#4A89E5'}"
                        :formatter.string="vueSliderFormatter"
                        :label-style="{'color' : '#fff'}"></vue-slider>
                </div>

                <button class="a-button" @click="resetMultiplicand">
                    Reset Multiplicand
                </button>

                <h2>Result</h2>
                <control before-after="pound-on-dark" label="Dependency Multiplicand">
                    <input autocomplete="off" :value="currencyFormat(total)" disabled
                           name="deceased_income"
                           type="text"/>
                </control>
            </div>
        </div>

        <div slot="results-panel" v-show="total > 0">
            <div class="p-animated-calc__results-panel u-full-width u-margin-auto">
                <h2>
                    Explanation
                </h2>

                <p>
                    The dependency multiplicand has been calculated as {{ currencyFormat(getValue('total')) }} based on the following:
                </p>

                <p>
                    The deceased was earning {{ currencyFormat(deceasedNetIncome) }} per annum.
                </p>

                <p>
                    There
                    {{ getValue('dependants').length == 1 ? 'was one dependant' : 'are ' }}
                    {{  getValue('dependants').length == 1 ? getValue('dependants').length + ' dependants' : ''}}
                    with the following income(s):
                </p>

                <ul style="margin-left: 40px;">
                    <li v-for="(item, index) in getValue('dependants')">
                        Dependant {{ index + 1 }} - {{ currencyFormat(item.income) }}
                    </li>
                </ul>

                <p>
                    And the multiplicand is set at {{ multiplicand }}%
                </p>

                <p>
                    The following formula was used to calculate the dependency multiplicand:
                </p>

                <div class="m-formula">
                    <div class="m-formula__item--row">
                        {{ vueSliderFormatter(multiplicand) }} x ([Deceased Income] + [Dependant Income]) - [Dependant Income]
                    </div>
                </div>

            </div>
        </div>
    </mini-calculator-page>
</template>

<script type="text/babel">
    import {mapGetters, mapActions} from 'vuex';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import DateInput from 'components/form/control/DateInput.vue';
    import DependantIncome from 'pages/mini-calculators/calculators/CalculatorComponents/DependantIncome.vue'
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import CalculatorKeyBindings from 'pages/mini-calculators/calculators/mixins/MiniCalculatorBindings';
    import MiniCalculatorPage from 'pages/mini-calculators/components/MiniCalculatorPage.vue';
    import _ from 'lodash';
    import VueSlider from 'vue-slider-component';
    import MiniCalcIcons from 'components/Icons.vue'
    import Intro from 'pages/mini-calculators/calculators/CalculatorComponents/Intro';
    import currencyFormat from 'mixins/currencyFormat';
    import MiniCalculatorHeader from 'pages/mini-calculators/components/MiniCalculatorHeader';
    import roundValue from 'mixins/roundValue';

    export default {
        name: 'dependency-multiplicand',
        created() {
            this.reset();
            if (this.countDependants >= 1) {
                return;
            }
            this.createDependant();
        },
        data() {
            return {
                sliderDisabled: false
            }
        },
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        computed: {
            ...mapGetters('mini/dependency', ['getValue', 'totalDependantIncome', 'countDependants', 'getMultiplicand']),
            deceasedNetIncome: {
                get() {
                    return this.getValue('deceasedIncome')
                },
                set(value) {
                    this.setValue({key: 'deceasedIncome', value})
                }
            },
            multiplicand: {
                get() {
                    return this.getMultiplicand;
                },
                set(value) {
                    this.setValue({key: 'multiplicand', value});
                }
            },
            total: {
                get() {
                    let total = this.getValue('total');

                    if (total && this.deceasedNetIncome > 0) {
                        this.hidePrompt();
                    }

                    return total;
                }
            }
        },
        watch: {
            ['custom.value']() {
                this.updateCustom();
            },
        },
        mixins: [
            FormValidator,
            CalculatorKeyBindings,
            currencyFormat
        ],
        components: {
            Control,
            ControlError,
            DateInput,
            DependantIncome,
            VueSlider,
            MiniCalculatorPage,
            MiniCalculatorHeader,
            MiniCalcIcons,
            Intro
        },
        methods: {
            ...mapActions('mini/dependency', ['setValue', 'createDependant', 'deleteDependant', 'calculateTotal', 'updateCustomMultiplicand', 'reset']),
            updateCustom() {
                this.updateCustomMultiplicand(this.custom);
            },
            addDependant() {
                this.createDependant();
                this.resetMultiplicand();
            },
            removeDependant(index) {
                if (this.countDependants === 1) {
                    return;
                }
                this.deleteDependant(index);
                this.resetMultiplicand();
            },
            vueSliderFormatter(value) {
                if (value === 66.6) {
                    return '⅔';
                }

                return value + '%';
            },
            resetMultiplicand()
            {
                this.multiplicand = 0;
            }
        }
    }
</script>



// WEBPACK FOOTER //
// dependencyMultiplicand.vue?0b9e8bc3